import React, { useEffect, useState, useMemo, SetStateAction, useRef } from 'react';
import { Table, Segment, Message, Loader, Modal, Icon } from 'semantic-ui-react';
import LocalStorage from '../../../lib/LocalStorage';
import config from '../../../common/repository/config';
import { io, Socket } from 'socket.io-client';
import Button from '../../elements/Button';
import FileLabel from '../../common/FileLabel/FileLabel';
import { toast } from '../../common/Toast';

interface Metadata {
  receiver_name: string;
  receiver_role: string;
  receiver_number: string;
  receiver_id: string;
  claim_number: string;
  case_id: string;
  message_id: string;
  insurance_company_name: string;
}
export interface SendChatPayload {
  caseId: string;
  receiverName: string;
  receiverRole: string;
  receiverNumber: string;
}

interface WhatsappNotification {
  [x: string]: any;
  _id: string;
  metadata: Metadata;
  message?: string;
  message_id?: string;
  status?: { type: string; time: string }[];
  by?: { name: string; role: string; by_type: string };
  contact?: { name: string; number: string };
}
interface Template {
  [x: string]: SetStateAction<string>;
  id: string;
  name: string;
}

const WhatsappMetadataTable: React.FC = () => {
  const [metadataList, setMetadataList] = useState<WhatsappNotification[]>([]);
  const [conversationMessages, setConversationMessages] = useState<WhatsappNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<string | null>(null);

  const [receiverId, setReceiverId] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [name, setName] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [sendMessage, setSendMessage] = useState("");
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [serverMessage, setServerMessage] = React.useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [receiverRole, setReceiverRole] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  //   const [selectedFile, setSelectedFile] = useState(null);
  const [sheet, setSheet] = useState<File | null>(null);
  const [selectedReceiver, setSelectedReceiver] = useState({
    name: "",
    role: "",
    number: "",
    caseId: "",
  });
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);

  const [isWindowOpen, setIsWindowOpen] = useState(true);
  const socket: Socket = useMemo(() => io(config.apiBaseURLSocket), []);

  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => console.log('Socket connected');
    socket.on('connect', handleConnect);

    return () => {
      socket.off('connect', handleConnect);
      socket.disconnect();
    };
  }, [socket]);

  useEffect(() => {
    const fetchMetadata = async () => {
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
      if (!token) {
        setError('Authentication token is missing.');
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${config.apiBaseURL}/notification/listWhatsappMessage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': token,
          },
        });

        const data = await response.json();
        if (data.success) {
          const metadataItems = data.data.items || [];

          // Filter items with metadata.case_id
          const filteredItems = metadataItems.filter(
            (item: any) => item.metadata && item.metadata.case_id
          );

          console.log("Filtered items with case_id:", filteredItems);

          // Extract just the case_ids if needed
          const caseIds = filteredItems.map((item: { metadata: { case_id: any; }; }) => item.metadata.case_id);
          console.log("Extracted case IDs:", caseIds);
          // caseIds will be an array like: ["66a785c8f05f98a614da4611", ...]

          // Set filtered items in state
          setMetadataList(filteredItems);

          // If you need to do something with the case IDs, you can do it here
          // For example, pass them to another function:
          // processCaseIds(caseIds);
        } else {
          setError(data.message || 'Failed to load metadata.');
        }
      } catch (err) {
        setError('An error occurred while fetching metadata.');
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  const fetchWhatsAppConversation = async (receiver: {
    [x: string]: any;
    name: string;
    role: string;
    number: string;
    caseId: string
  }) => {
    setLoading(true);
    setError(null);
    try {
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
      if (!token) {
        setError("Authentication token is missing.");
        setLoading(false);
        return;
      }
      //   const caseId = window.location.href.split("/").pop();
      const response = await fetch(
        `${config.apiBaseURL}/whatsapp/listConversation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": token,
          },
          body: JSON.stringify({
            caseId: receiver.caseId,
            receiverRole: receiver.role,
            receiverNumber: receiver.number,
            receiverName: receiver.name,
          }),
        }
      );


      const result = await response.json();
      console.log(result, "result")

      const windowOpenFlag = result?.data?.items?.[0]?.window_open;

      setIsWindowOpen(windowOpenFlag === true || windowOpenFlag === "true");

      setIsWindowOpen(windowOpenFlag === true);
      if (!response.ok) {
        setServerMessage(result.message || "Something went wrong");
      } else {
        setServerMessage("");
        setMessages(result.data?.items || []);
      }
      const items = result?.data?.items || [];
      const receiverIdFromResult = items?.receiver_id || "";
      setOpen(true);

      if (socket) {
        const receiverIdFromResult = result?.data?.items?.[0]?.receiver_id || "";
        setReceiverId(receiverIdFromResult);
        socket.emit("join_room",
          receiverIdFromResult,
        );
        socket.off("new_message");
        socket.on("new_message", (msg) => {
          console.log("Real-time message:", msg);

          if (msg.status_type === "read") {
            setMessages((prevMessages) =>
              prevMessages.map((m) =>
                m.message_id === msg.message_id
                  ? { ...m, status_type: "read" }
                  : m
              )
            );
          } else {

            if (
              Array.isArray(msg) &&
              msg.length > 0 &&
              msg[0]?.conversation &&
              Array.isArray(msg[0].conversation) &&
              msg[0].conversation.length > 0
            ) {
              setMessages((prev) => [...prev, msg[0].conversation[0]]);
            } else {
              console.warn("Invalid or missing message format:", msg);
            }

          }
        });
      }
      setReceiverRole(receiver.role.toUpperCase());
      const conversation = result?.data?.items?.[0]?.conversation || [];
      setMessages(conversation);
      setSelectedReceiver(receiver);
      setOpen(true);
    } catch (error) {
      console.error("WhatsApp listing error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlechatDownload = async (actualReceiverId: string) => {
    setDownloading(true);
    try {
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
      if (!token) {
        console.error("Authentication token is missing.");
        return;
      }

      const response = await fetch(`${config.apiBaseURL}/whatsapp/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({ receiverId: actualReceiverId }),
      });

      if (!response.ok)
        throw new Error(`Failed to download: ${response.statusText}`);
      const disposition = response.headers.get("Content-Disposition");
      let fileName = "WhatsAppChat.zip";

      if (disposition && disposition.includes("attachment")) {
        const match = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (match != null && match[1]) {
          fileName = match[1].replace(/['"]/g, "");
        }
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error in handlechatDownload:", error);
    } finally {
      setDownloading(false);
    }
  };
  const handleTemplateSelect = async (templateName: string, receiver: {
    name: string;
    role: string;
    number: string;
  }) => {
    try {
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
      if (!token) throw new Error("Authentication token is missing.");

      const caseId = window.location.href.split("/").pop();
      if (!caseId || !receiver.name || !receiver.role || !receiver.number) {
        toast.error("Missing required data to send the message.");
        return;
      }

      const payload = {
        caseId,
        receiverName: receiver.name,
        receiverRole: receiver.role,
        receiverNumber: receiver.number,
        templateName,
      };

      console.log("Sending template message:", payload);

      const response = await fetch(`${config.apiBaseURL}/whatsapp/sendTemplate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("Manual send result:", result);

      if (!response.ok) {
        toast.error(result?.message || "Failed to send template.");
        return;
      }

      toast.success("Template sent successfully.");
    } catch (error) {
      console.error("Send error:", error);
      toast.error("Failed to send the template.");
    }
  };

  const handleSendMessage = async () => {
    if (!sendMessage || !selectedReceiver?.number) {
      toast.error("Please enter a message and select a receiver.");
      return;
    }

    const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
    if (!token) {
      toast.error("Authentication token is missing.");
      return;
    }

    const caseId = window.location.href.split("/").pop();
    if (!caseId || !selectedReceiver.name || !selectedReceiver.role || !selectedReceiver.number) {
      toast.error("Missing required data to send the message.");
      return;
    }

    const payload = {
      caseId: selectedReceiver.caseId,
      msg: sendMessage,
      receiverName: selectedReceiver.name,
      receiverRole: selectedReceiver.role,
      receiverNumber: selectedReceiver.number
    };

    try {
      const response = await fetch(`${config.apiBaseURL}/whatsapp/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log("Message send result:", result);

      if (!response.ok) {
        toast.error(result?.message || "Failed to send message.");
        return;
      }
      fetchWhatsAppConversation(selectedReceiver);
      // toast.success("Message sent successfully.");
      setSendMessage("");
    } catch (error) {
      console.error("Send error:", error);
      toast.error("Failed to send the message.");
    }
  };

  const handleSendImage = async () => {
    const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
    if (!token) {
      toast.error("Authentication token is missing.");
      return;
    }

    const caseId = selectedReceiver.caseId || window.location.href.split("/").pop();
    if (!caseId || !selectedReceiver.name || !selectedReceiver.role || !selectedReceiver.number || !sheet) {
      toast.error("Missing required data to send the message.");
      return;
    }

    const formData = new FormData();
    formData.append("caseId", caseId);
    formData.append("receiverName", selectedReceiver.name);
    formData.append("receiverRole", selectedReceiver.role);
    formData.append("receiverNumber", selectedReceiver.number);
    formData.append("sheet", sheet);

    try {
      const response = await fetch(`${config.apiBaseURL}/whatsapp/sendMedia`, {
        method: "POST",
        headers: {
          "x-access-token": token,
        },
        body: formData,
      });

      const result = await response.json();
      console.log("Message send result:", result);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Reset file input
      }
      if (!response.ok) {
        toast.error(result?.message || "Failed to send message.");
        return;
      }

      fetchWhatsAppConversation(selectedReceiver);
      setSendMessage("");
    } catch (error) {
      console.error("Send error:", error);
      toast.error("Failed to send the message.");
    }
  };


  const handleTemplateClick = async () => {
    console.log("Select Template clicked");
    setShowOptions(false);
    setShowTemplateList(false);
    setLoading(true);
    setNoDataMessage(null);
    setTemplates([]);

    try {
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
      if (!token) throw new Error("Authentication token is missing.");

      if (!receiverRole) {
        setNoDataMessage("Receiver role not found.");
        setLoading(false);
        return;
      }

      const payload = {
        type: receiverRole,
        status: recordingStatus === "BUSY" ? "ANSWERED" : "NOT ANSWERED",
      };
      console.log(receiverRole, "lower Case", recordingStatus)

      const response = await fetch(`${config.apiBaseURL}/template`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log(result, "this is result");

      if (!response.ok) {
        const errorMessage = result?.message || "Failed to fetch templates.";
        setNoDataMessage(errorMessage);
        return;
      }

      const items: Template[] = result?.data?.items || [];
      if (items.length === 0) {
        setNoDataMessage(result?.message || "No templates found.");
        return;
      }

      setTemplates(items);
      setShowTemplateList(true);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Segment padded="very">
        <h3>WhatsApp Message</h3>
        {loading && <Loader active inline="centered" content="Loading messages..." />}
        {error && <Message negative content={error} />}
        {!loading && !error && (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Receiver Name</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>Mobile</Table.HeaderCell>
                <Table.HeaderCell>Claim</Table.HeaderCell>
                <Table.HeaderCell>Insurance Company</Table.HeaderCell>
                <Table.HeaderCell>Action</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {metadataList.map((msg) => (
                <Table.Row key={msg._id}>
                  <Table.Cell>{msg.metadata?.receiver_name || 'N/A'}</Table.Cell>
                  <Table.Cell>{msg.metadata?.receiver_role || 'N/A'}</Table.Cell>
                  <Table.Cell>{msg.metadata?.receiver_number || 'N/A'}</Table.Cell>
                  {/* <Table.Cell>{msg.metadata?.case_id || 'N/A'}</Table.Cell> */}
                  <Table.Cell>{msg.metadata?.claim_number || 'N/A'}</Table.Cell>
                  <Table.Cell>{msg.metadata?.insurance_company_name || 'N/A'}</Table.Cell>
                  <Table.Cell>
                    <i
                      className="fab fa-whatsapp"
                      style={{ color: '#25D366', fontSize: '36px', cursor: 'pointer' }}
                      onClick={() => {
                        setName(msg.metadata.receiver_name);
                        fetchWhatsAppConversation({
                          name: msg.metadata.receiver_name,
                          role: msg.metadata.receiver_role,
                          number: msg.metadata.receiver_number,
                          caseId: msg.metadata.case_id,
                        });
                      }}
                    ></i>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      <Modal
        open={open}
        onClose={() => {
          if (socket && receiverId) socket.emit('leave_chat', receiverId);
          setOpen(false);
        }}
        size="small"
        style={{
          backgroundImage: 'url(/whatsapp.jpeg)',
          backgroundSize: 'cover',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '350px',
          height: '500px',
          margin: '0',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.05)',
          padding: '0',
          zIndex: 1001,
        }}
      >
        <Modal.Content
          style={{
            padding: '0',
            backgroundColor: 'transparent',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {/* Header */}
          <div
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#075E54", color: "#fff", padding: "10px 15px", fontWeight: "bold", fontSize: "16px", borderTopLeftRadius: "20px", borderTopRightRadius: "20px" }}

          >
            {open && <div>{name}</div>}
            <Icon
              className="download"
              onClick={() => handlechatDownload(receiverId)}
              disabled={downloading}
              loading={downloading}
            ></Icon>
            <Button
              icon="close"
              onClick={() => {
                if (socket && receiverId) {
                  socket.emit("leave_chat", receiverId);
                  console.log("Left chat with receiverId:", receiverId);
                }
                setOpen(false);
              }}
              style={{
                backgroundColor: "transparent", color: "#FF0000", boxShadow: "none",
              }}
            />
          </div>

          {/* Chat Body */}
          <div
            style={{
              flexGrow: 1,
              padding: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              overflowY: "auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {messages.length === 0 && serverMessage ? (
              <p>{serverMessage}</p>
            ) : null}

            {messages.map((msg, index) => {
              const isSender = msg.by?.by_type === "sender";
              const status = msg.status?.[0];
              const isSent = status?.type === "sent";
              const time = status?.time;

              return (
                <div
                  key={msg.message_id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isSender ? "flex-end" : "flex-start",
                    maxWidth: "75%",
                    marginRight: isSender ? "0" : "auto",
                    marginLeft: isSender ? "auto" : "0",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: isSender ? "#DCF8C6" : "#FFFFFF",
                      padding: "10px 15px",
                      borderRadius: "10px",
                      boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                      color: "#000",
                      wordBreak: "break-word",
                      width: "100%",
                      position: "relative",
                    }}
                  >

                    {(msg.by?.name || msg.by?.role) && (
                      <div style={{ marginBottom: "6px" }}>
                        <div>
                          <span style={{ fontSize: "13px", fontWeight: 600, color: "#128C7E" }}>
                            <b>{msg.by?.name}</b>
                          </span>
                          {msg.by?.role && (
                            <span style={{ fontSize: "12px", color: "#555", marginLeft: "6px" }}>
                              ({msg.by.role})
                            </span>
                          )}
                        </div>

                      </div>
                    )}





                    {/* message content */}
                    <>
                      {/* FileLabel group for image */}
                      {/* {(msg.message && /\.(jpg|jpeg|png|webp|gif|mp4|mp3|docx|ogg)$/i.test(msg.message.trim())) || msg.image ? ( */}
                      {(/\.[^.\s]{2,5}$/i.test(msg.message?.trim() || "") || msg.image) ? (
                        <FileLabel.Group horizontal>
                          {/* File from msg.message */}
                          {msg.message && (
                            <FileLabel
                              title={msg.message.trim().split("/").pop()?.slice(0, 30) || "File"}
                              link={`http://stg-verifacto.amable.in/${msg.message.trim()}`}
                              color="#2185d0"
                              removable={false}
                            />
                          )}

                          {/* File from msg.image */}
                          {msg.image && (
                            <FileLabel
                              title={msg.image.trim().split("/").pop()?.slice(0, 30) || "Image"}
                              link={msg.image.trim()}
                              color="#2185d0"
                              removable={false}
                            />
                          )}
                        </FileLabel.Group>
                      ) : (
                        <span>
                          {msg.message}
                          {msg.message_subject && ` ${msg.message_subject}`}
                        </span>
                      )}

                      {/* Contact */}
                      {msg.contact && (
                        <>
                          {" "}
                          | <span>{`${msg.contact.name} (${msg.contact.number})`}</span>
                        </>
                      )}

                      {/* Location */}
                      {msg.location && (
                        <>

                          <FileLabel
                            title="📍 View Location"
                            link={`https://www.google.com/maps?q=${msg.location.latitude},${msg.location.longitude}`}
                            color="#f2711c"
                            removable={false}
                          />
                        </>
                      )}
                    </>


                    {/* reactions */}
                    {msg.reactions && msg.reactions.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          marginTop: "6px",
                          flexWrap: "wrap",
                        }}
                      >
                        {msg.reactions.map((reaction: { _id: any; reacted_by: { name: any; role: any; }; emoji: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined; }, i: any) => (
                          <div
                            key={reaction._id || i}
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 8px", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 1px 2px rgba(0,0,0,0.1)", fontSize: "15px", cursor: "default" }}

                            title={`${reaction.reacted_by?.name} (${reaction.reacted_by?.role || "User"})`}
                          >
                            {reaction.emoji}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* timestamp and read status */}
                    {msg.status && msg.status.length > 0 && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#777",
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          marginTop: "6px",
                        }}
                      >
                        <span>{time}</span>

                        {isSender && (() => {
                          const latestStatus = msg.status[msg.status.length - 1]?.type;

                          let icon = "✔";
                          let color = "#D6D3C7"; // default gray

                          if (latestStatus === "read") {
                            icon = "✔✔";
                            color = "#34B7F1"; // blue colour
                          } else if (latestStatus === "delivered") {
                            icon = "✔✔";
                            color = "#D6D3C7"; // gray colour
                          } else if (latestStatus === "sent") {
                            icon = "✔";
                            color = "#D6D3C7"; // gray colour
                          }
                          return (
                            <span style={{ fontSize: "14px", marginLeft: "6px", color }}>{icon}</span>
                          );
                        })()}
                      </div>
                    )}
                    {!isSender && msg.created_on && (
                      <div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
                        {msg.created_on}
                      </div>
                    )}
                    {/* star icon */}
                    {/* {!isSender && (
                      <Icon
                        name={msg.starred ? "star" : "star outline"}
                        color={msg.starred ? "yellow" : "grey"}
                        style={{ position: "absolute", bottom: "6px", right: "8px", cursor: "pointer", fontSize: "1.2em" }}

                        title={
                          msg.starred ? "Unstar message" : "Star message"
                        }
                        onClick={() => toggleStar(msg.message_id)}
                      />
                    )} */}
                  </div>
                </div>
              );
            })}
          </div>


          {/* Footer */}

          <div
            style={{ display: "flex", alignItems: "center", padding: "10px", borderBottomLeftRadius: "20px", borderBottomRightRadius: "20px", position: "relative", backgroundColor: "rgba(255, 255, 255, 0.85)", zIndex: 10 }}

          >
            {/* Chat Input Box */}
            <div
              style={{ display: "flex", alignItems: "center", flex: 1, padding: "8px 12px", borderRadius: "25px", marginRight: "10px", backgroundColor: "#fff", position: "relative" }}

            >
              {/* Options Icon */}
              <div style={{ position: "relative" }}>
                <div
                  onClick={() => setShowOptions((prev) => !prev)}
                  style={{
                    cursor: "pointer",
                    fontSize: "24px",
                    padding: "0 8px",
                    userSelect: "none",
                  }}
                >
                  +
                </div>

                {/* Options Dropdown */}
                {showOptions && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "30px",
                      left: 0,
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      borderRadius: "8px",
                      zIndex: 100,
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ fontFamily: "sans-serif", padding: "16px" }}>
                      {/* File Picker */}
                      <label
                        htmlFor="fileInput"
                        style={{
                          display: "inline-block",
                          padding: "10px 16px",
                          backgroundColor: "#f0f0f0",
                          borderRadius: "6px",
                          cursor: "pointer",
                          border: "1px solid #ccc",
                          fontWeight: "bold",
                          marginBottom: "12px",
                        }}
                      >
                        📁 Choose Image
                      </label>
                      <input
                        id="fileInput"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setSheet(e.target.files[0]);
                            setPreviewURL(URL.createObjectURL(e.target.files[0])); // make sure you define this in your state
                          }
                        }}
                      />

                      {/* Preview */}
                      {previewURL && (
                        <div style={{ marginTop: "16px" }}>
                          <img
                            src={previewURL}
                            alt="Preview"
                            style={{
                              width: "160px",
                              height: "auto",
                              borderRadius: "8px",
                              border: "1px solid #ddd",
                              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                        </div>
                      )}


                    </div>
                    <div
                      onClick={handleTemplateClick}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Select Template
                    </div>
                  </div>
                )}
                {showTemplateList && templates.length > 0 && (
                  <div
                    style={{ position: "absolute", bottom: "80px", left: 0, backgroundColor: "#f9f9f9", border: "1px solid #ccc", borderRadius: "8px", maxHeight: "200px", overflowY: "auto", zIndex: 101, width: "300px" }}
                  >
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => {
                          setSelectedTemplateName(template.name);
                          setSendMessage(template.name);
                          setTemplates((prevTemp) =>
                            prevTemp.filter((t) => t.id !== template.id)
                          );

                        }}

                      >
                        {template.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Message Input */}
              <input
                type="text"
                value={sendMessage}
                placeholder="Type a message"
                onChange={e => setSendMessage(e.target.value)}
                disabled={!isWindowOpen}
                // allow editing input
                style={{
                  flex: 1, padding: "10px", border: "none", borderRadius: "20px", fontSize: "14px", outline: "none", backgroundColor: isWindowOpen ? "transparent" : "#f0f0f0",
                  cursor: isWindowOpen ? "text" : "not-allowed",
                }}
              />
            </div>

            {/* Send Button */}
            <button
              onClick={async () => {
                if (selectedTemplateName) {
                  setIsProcessing(true);
                  handleTemplateSelect(selectedTemplateName, selectedReceiver);

                }
                if (sheet) {
                  console.log("Sheet exists, calling handleSendImage", sheet);
                  await handleSendImage();
                  setSheet(null);
                  setPreviewURL(null);
                  setShowOptions(false);
                  setShowTemplateList(false);
                  setSelectedTemplateName("");

                  if (fileInputRef.current) {
                    fileInputRef.current.value = ""; // Reset file input
                  }
                }

                if (sendMessage.trim()) {
                  handleSendMessage();
                }

                setSendMessage("");
                setSelectedTemplateName("");
                setShowTemplateList(false);
                setIsProcessing(false);
              }}
              style={{
                border: "none",
                borderRadius: "50%",
                padding: "12px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#25D366",
                color: "#fff",
              }}
            >
              <i className="send icon" style={{ fontSize: "16px" }}></i>
            </button>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default WhatsappMetadataTable;
